import { useState } from 'react';
import { Button } from '../Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../Card';
import { Input, InputWrapper, Label } from '../Input';
import { Modal } from '../Modal';
import { AiLogoGenerator } from '../AiLogoGenerator';
import { apiClient } from '../../services/apiClient';
import { removeWhiteBackground, blobToLogoFile } from '../../utils/removeLogoBackground';
import { useWebsiteScraper } from '../../hooks/workspacePage/useWebsiteScraper';
import { SectionGrid, useBrandContext } from './brandShared';
import {
  AssetGrid,
  AssetItem,
  DeleteButton,
  LogoBadge,
  SetLogoButton,
  AddAssetForm,
  EmptyAssets,
  OrDivider,
  FileInput,
  FileInputLabel,
  SelectedFile,
  ClearFileButton,
  UploadErrors,
} from './assetsStyled';

// Límite de archivos por tanda (igual que en WorkspacePage).
const MAX_FILES = 10;

/**
 * Tab "Assets" del Brand Page unificado.
 * - Brand Assets: multi-upload (máx 10, progreso + errores por tanda),
 *   alta por URL (nombre + url), grid con badge de logo, "Set as Logo", borrar.
 * - Background Images: multi-upload (máx 10), alta por URL, grid + borrar.
 * Todo el estado/acciones vienen de ctx.assets / ctx.backgrounds.
 */
export function BrandTabAssets() {
  const { assets, backgrounds, workspaceId, workspace, reloadWorkspace, form } = useBrandContext();

  // ─── Generar logo con IA ─────────────────────────────────────────────────────
  const [showAiLogo, setShowAiLogo] = useState(false);
  const [logoSaving, setLogoSaving] = useState(false);
  const [logoErr, setLogoErr] = useState('');

  // ─── Backfill: escanear con IA los assets viejos (los nuevos se escanean solos
  //     al subir). Llama /scan-missing en loop hasta que no queden pendientes. ──
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState('');
  const handleScanAssets = async () => {
    if (!workspaceId || scanning) return;
    setScanning(true);
    setScanMsg('Scanning photos with AI…');
    try {
      let total = 0;
      let noProvider = false;
      // Tope de seguridad: 30 tandas (12 c/u) = hasta 360 assets por corrida.
      for (let i = 0; i < 30; i++) {
        const res = await apiClient.post<{ scanned: number; remaining: number; noProvider?: boolean }>(
          `/api/workspaces/${workspaceId}/assets/scan-missing`, {},
        );
        if (res?.noProvider) { noProvider = true; break; }
        total += res?.scanned || 0;
        if (!res || res.remaining <= 0) break;
        setScanMsg(`Scanning… ${total} done, ${res.remaining} to go`);
      }
      await Promise.all([assets.fetchAssets(), fetchBackgroundImages()]);
      if (noProvider) setScanMsg('AI vision not configured — no OpenAI key.');
      else setScanMsg(total > 0 ? `Scanned ${total} photo${total > 1 ? 's' : ''} ✓` : 'All photos already scanned ✓');
    } catch (err) {
      setScanMsg(err instanceof Error ? err.message : 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  // El logo de IA llega recortado (PNG transparente) como File → lo subimos como
  // asset con isLogo=true (el endpoint deja de logo a cualquier otro).
  const handleAiLogoReady = async (file: File, _previewUrl: string, extras: string[] = []) => {
    if (!workspaceId) return;
    setLogoSaving(true);
    setLogoErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('isLogo', 'true');
      await apiClient.upload(`/api/workspaces/${workspaceId}/assets/upload`, fd);
      // Las otras variantes IA (no elegidas) → assets extra (isLogo=false), para
      // tener varias versiones del logo disponibles en el editor.
      for (const url of extras) {
        try {
          const blob = await removeWhiteBackground(url);
          const extraFile = blobToLogoFile(blob, (file.name.replace(/\.[^.]+$/, '') || 'logo'));
          const efd = new FormData();
          efd.append('file', extraFile);
          efd.append('isLogo', 'false');
          await apiClient.upload(`/api/workspaces/${workspaceId}/assets/upload`, efd);
        } catch { /* non-fatal */ }
      }
      await assets.fetchAssets();
      await reloadWorkspace();
      setShowAiLogo(false);
    } catch (err) {
      setLogoErr(err instanceof Error ? err.message : 'Could not save the logo');
    } finally {
      setLogoSaving(false);
    }
  };

  const {
    assets: assetList,
    assetUrl,
    setAssetUrl,
    assetName,
    setAssetName,
    isAddingAsset,
    assetFiles,
    setAssetFiles,
    isUploadingAsset,
    uploadProgress,
    uploadErrors,
    handleAddAsset,
    handleDeleteAsset,
    handleSetLogo,
    handleAssetFileUpload,
  } = assets;

  const {
    backgroundImages,
    bgImageFiles,
    setBgImageFiles,
    isUploadingBgImage,
    bgUploadProgress,
    bgUploadErrors,
    bgImageUrl,
    setBgImageUrl,
    bgImageName,
    setBgImageName,
    isAddingBgImage,
    handleBackgroundImageUpload,
    handleDeleteBackgroundImage,
    handleAddBackgroundImageUrl,
    fetchBackgroundImages,
  } = backgrounds;

  // ─── Escanear una web en busca de fotos (misma función del onboarding) ───────
  //     Reusa useWebsiteScraper: solo-imágenes → grid de selección → alta como
  //     brand assets o backgrounds (delega en los handlers de los hooks de arriba).
  const scraper = useWebsiteScraper({
    workspaceId,
    setBrandInfo: () => {},                                   // acá no traemos brand info, solo fotos
    addSelectedAsAssets: assets.handleAddSelectedImages,
    addSelectedAsBackgrounds: backgrounds.handleAddSelectedAsBackgrounds,
    isAddingSelectedAsBackgrounds: backgrounds.isAddingSelectedAsBackgrounds,
  });

  // Guardas de cantidad: alertan y resetean el input si superan el máximo.
  const onAssetFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > MAX_FILES) {
      alert(`Max ${MAX_FILES} images at a time. Please select up to ${MAX_FILES} files.`);
      e.target.value = '';
      return;
    }
    setAssetFiles(files);
  };

  const onBgFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > MAX_FILES) {
      alert(`Max ${MAX_FILES} images at a time. Please select up to ${MAX_FILES} files.`);
      e.target.value = '';
      return;
    }
    setBgImageFiles(files);
  };

  return (
    <SectionGrid>
      {/* ─── Brand Assets ─────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Brand Assets</CardTitle>
            <CardDescription>Upload images or add URLs for your brand</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAssetFileUpload}>
            <FileInput
              type="file"
              id="assetFile"
              accept="image/*"
              multiple
              onChange={onAssetFilesPicked}
            />
            {assetFiles.length === 0 ? (
              <FileInputLabel htmlFor="assetFile">
                Click to upload images (multiple allowed)
              </FileInputLabel>
            ) : (
              <SelectedFile>
                <span>
                  {assetFiles.length} file{assetFiles.length > 1 ? 's' : ''} selected
                </span>
                <ClearFileButton
                  type="button"
                  onClick={() => setAssetFiles([])}
                  aria-label="Clear selected files"
                >
                  <span aria-hidden="true">×</span>
                </ClearFileButton>
              </SelectedFile>
            )}
            <Button
              type="submit"
              $fullWidth
              disabled={isUploadingAsset || assetFiles.length === 0}
              style={{ marginTop: 16 }}
            >
              {isUploadingAsset && uploadProgress
                ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                : `Upload Image${assetFiles.length > 1 ? 's' : ''}`}
            </Button>
            {uploadErrors.length > 0 && (
              <UploadErrors>
                Failed to upload: {uploadErrors.join(', ')}. Try uploading fewer files or paste a
                URL instead.
              </UploadErrors>
            )}
          </form>

          <OrDivider>or add image URL</OrDivider>

          <AddAssetForm onSubmit={handleAddAsset}>
            <InputWrapper>
              <Label htmlFor="assetName">Asset Name</Label>
              <Input
                id="assetName"
                type="text"
                value={assetName}
                onChange={(e) => setAssetName(e.target.value)}
                placeholder="e.g., Main Logo"
              />
            </InputWrapper>
            <InputWrapper>
              <Label htmlFor="assetUrl">Image URL</Label>
              <Input
                id="assetUrl"
                type="url"
                value={assetUrl}
                onChange={(e) => setAssetUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
              />
            </InputWrapper>
            <Button type="submit" $fullWidth disabled={isAddingAsset || !assetUrl || !assetName}>
              {isAddingAsset ? 'Adding...' : 'Add Asset'}
            </Button>
          </AddAssetForm>

          <OrDivider>or let AI design one</OrDivider>

          <Button type="button" $variant="outline" $fullWidth onClick={() => setShowAiLogo(true)}>
            ✨ Generate a logo with AI
          </Button>
          {logoErr && <UploadErrors>{logoErr}</UploadErrors>}

          <OrDivider>or find images on a website</OrDivider>

          <form onSubmit={scraper.handleScrapeImagesOnly}>
            <InputWrapper>
              <Label htmlFor="imageScrapeUrl">Website URL</Label>
              <Input
                id="imageScrapeUrl"
                type="url"
                value={scraper.imageScrapeUrl}
                onChange={(e) => scraper.setImageScrapeUrl(e.target.value)}
                placeholder="https://yourbrand.com"
              />
            </InputWrapper>
            <Button type="submit" $variant="outline" $fullWidth disabled={scraper.isScrapingImages || !scraper.imageScrapeUrl}>
              {scraper.isScrapingImages ? 'Scanning website…' : '🔎 Find Images on Website'}
            </Button>
          </form>

          {scraper.imageScrapeMessage && scraper.scrapedImages.length === 0 && (
            <p style={{ fontSize: 12, color: '#64748B', marginTop: 8, textAlign: 'center' }}>
              {scraper.imageScrapeMessage}
            </p>
          )}

          {scraper.scrapedImages.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <strong style={{ fontSize: 13 }}>
                  {scraper.scrapedImages.length} image{scraper.scrapedImages.length > 1 ? 's' : ''} found
                  {scraper.selectedImages.size > 0 ? ` · ${scraper.selectedImages.size} selected` : ''}
                </strong>
                <button
                  type="button"
                  onClick={scraper.dismissScrapedImages}
                  style={{ background: 'none', border: 'none', color: '#64748B', fontSize: 12, cursor: 'pointer' }}
                >
                  Dismiss
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
                {scraper.scrapedImages.map((img) => {
                  const sel = scraper.selectedImages.has(img);
                  return (
                    <button
                      key={img}
                      type="button"
                      onClick={() => scraper.toggleImageSelection(img)}
                      style={{ position: 'relative', padding: 0, border: sel ? '2px solid #2563EB' : '2px solid var(--border, #E5E7EB)', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: 'none', aspectRatio: '1 / 1' }}
                    >
                      <img src={img} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      {sel && (
                        <span style={{ position: 'absolute', top: 4, right: 4, width: 18, height: 18, borderRadius: 999, background: '#2563EB', color: '#fff', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Button
                  type="button"
                  $fullWidth
                  disabled={scraper.selectedImages.size === 0 || scraper.isAddingSelectedImages}
                  onClick={scraper.handleAddSelectedImages}
                >
                  {scraper.isAddingSelectedImages ? 'Adding…' : `Add ${scraper.selectedImages.size || ''} as Assets`.replace('  ', ' ')}
                </Button>
                <Button
                  type="button"
                  $variant="outline"
                  $fullWidth
                  disabled={scraper.selectedImages.size === 0 || scraper.isAddingSelectedAsBackgrounds}
                  onClick={scraper.handleAddSelectedAsBackgrounds}
                >
                  {scraper.isAddingSelectedAsBackgrounds ? 'Adding…' : 'Add as Backgrounds'}
                </Button>
              </div>
            </div>
          )}

          {assetList.length > 0 ? (
            <AssetGrid>
              {assetList.map((asset) => (
                <AssetItem key={asset.id} $isLogo={asset.isLogo}>
                  <img src={asset.url} alt={asset.name} title={asset.aiDescription || asset.name} />
                  {asset.isLogo && <LogoBadge>Logo</LogoBadge>}
                  {!asset.isLogo && (
                    <SetLogoButton
                      type="button"
                      onClick={() => handleSetLogo(asset.id)}
                      aria-label={`Set ${asset.name} as logo`}
                    >
                      Set as Logo
                    </SetLogoButton>
                  )}
                  <DeleteButton
                    type="button"
                    onClick={() => handleDeleteAsset(asset.id)}
                    aria-label={`Delete asset ${asset.name}`}
                  >
                    <span aria-hidden="true">×</span>
                  </DeleteButton>
                </AssetItem>
              ))}
            </AssetGrid>
          ) : (
            <EmptyAssets>No assets added yet. Add image URLs above.</EmptyAssets>
          )}
        </CardContent>
      </Card>

      {/* ─── Background Images ────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Background Images</CardTitle>
            <CardDescription>Upload photos to use as post backgrounds</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleBackgroundImageUpload}>
            <FileInput
              type="file"
              id="bgImageFile"
              accept="image/*"
              multiple
              onChange={onBgFilesPicked}
            />
            {bgImageFiles.length === 0 ? (
              <FileInputLabel htmlFor="bgImageFile">
                Click to upload photos (multiple allowed)
              </FileInputLabel>
            ) : (
              <SelectedFile>
                <span>
                  {bgImageFiles.length} file{bgImageFiles.length > 1 ? 's' : ''} selected
                </span>
                <ClearFileButton
                  type="button"
                  onClick={() => setBgImageFiles([])}
                  aria-label="Clear selected files"
                >
                  <span aria-hidden="true">×</span>
                </ClearFileButton>
              </SelectedFile>
            )}
            <Button
              type="submit"
              $fullWidth
              disabled={isUploadingBgImage || bgImageFiles.length === 0}
              style={{ marginTop: 16 }}
            >
              {isUploadingBgImage && bgUploadProgress
                ? `Uploading ${bgUploadProgress.current}/${bgUploadProgress.total}...`
                : `Upload Photo${bgImageFiles.length > 1 ? 's' : ''}`}
            </Button>
            {bgUploadErrors.length > 0 && (
              <UploadErrors>
                Failed to upload: {bgUploadErrors.join(', ')}. Try uploading fewer files or paste a
                URL instead.
              </UploadErrors>
            )}
          </form>

          <OrDivider>or add image URL</OrDivider>

          <AddAssetForm onSubmit={handleAddBackgroundImageUrl}>
            <InputWrapper>
              <Label htmlFor="bgImageName">Image Name</Label>
              <Input
                id="bgImageName"
                type="text"
                value={bgImageName}
                onChange={(e) => setBgImageName(e.target.value)}
                placeholder="e.g., Office Photo"
              />
            </InputWrapper>
            <InputWrapper>
              <Label htmlFor="bgImageUrl">Image URL</Label>
              <Input
                id="bgImageUrl"
                type="url"
                value={bgImageUrl}
                onChange={(e) => setBgImageUrl(e.target.value)}
                placeholder="https://example.com/photo.jpg"
              />
            </InputWrapper>
            <Button type="submit" $fullWidth disabled={isAddingBgImage || !bgImageUrl || !bgImageName}>
              {isAddingBgImage ? 'Adding...' : 'Add Background Image'}
            </Button>
          </AddAssetForm>

          {(backgroundImages.length > 0 || assetList.some((a) => !a.isLogo)) && (
            <>
              <OrDivider>AI photo library (for green-zone auto-fill)</OrDivider>
              <Button type="button" $variant="outline" $fullWidth onClick={handleScanAssets} disabled={scanning}>
                {scanning ? 'Scanning…' : '🔍 Scan photos with AI'}
              </Button>
              <p style={{ fontSize: 11, color: '#64748B', marginTop: 6, textAlign: 'center' }}>
                {scanMsg || 'New uploads are scanned automatically. Run this once to catalog your existing backgrounds & brand photos so AI can auto-pick the best one for the green zone.'}
              </p>
            </>
          )}

          {backgroundImages.length > 0 ? (
            <AssetGrid>
              {backgroundImages.map((image) => (
                <AssetItem key={image.id}>
                  <img src={image.url} alt={image.name} title={image.aiDescription || image.name} />
                  <DeleteButton
                    type="button"
                    onClick={() => handleDeleteBackgroundImage(image.id)}
                    aria-label={`Delete background image ${image.name}`}
                  >
                    <span aria-hidden="true">×</span>
                  </DeleteButton>
                </AssetItem>
              ))}
            </AssetGrid>
          ) : (
            <EmptyAssets>
              No background images yet. Upload photos or add URLs above to use as post backgrounds.
            </EmptyAssets>
          )}
        </CardContent>
      </Card>

      {/* Modal de generación de logo con IA */}
      <Modal
        isOpen={showAiLogo}
        onClose={() => { if (!logoSaving) setShowAiLogo(false); }}
        title="Generate a logo with AI"
        maxWidth="460px"
      >
        <AiLogoGenerator
          brandName={form.formData.workspaceName || workspace?.name || ''}
          colors={form.formData.brandColors}
          industry={form.formData.productsServices?.join(', ')}
          description={form.formData.description}
          onLogoReady={handleAiLogoReady}
        />
        {logoSaving && (
          <p style={{ fontSize: 12, color: '#64748B', marginTop: 10, textAlign: 'center' }}>Saving your logo…</p>
        )}
      </Modal>
    </SectionGrid>
  );
}

export default BrandTabAssets;
